import { useEffect, useRef } from 'react';

import { useFormActions, useFormValues } from '../core';
import type { FormField, FormInitialValues } from '../typings';
import { collectClearableFieldKeys } from '../utils/clearableFieldKeys';
import { shouldClearFieldValue } from '../utils/clearedFieldValue';

/** Wipes an item's values, and every descendant's, the moment its Not Applicable box is ticked. */
export const useClearOnNotApplicable = (
  field: FormField,
  isFieldNA: boolean,
): void => {
  const values = useFormValues<FormInitialValues>();
  const { setFieldValue } = useFormActions();
  // Seeded from the current state so a form opened with NA already set never writes.
  const wasFieldNA = useRef(isFieldNA);
  const valuesRef = useRef(values);

  // Formik returns a fresh values object on every keystroke anywhere in the form. Mirroring it
  // into a ref keeps it out of the dependency array below, so the clearing effect runs when the
  // checkbox flips rather than on every edit in the section. Declared first so the mirror is
  // current by the time the clearing effect reads it.
  useEffect(() => {
    valuesRef.current = values;
  });

  useEffect(() => {
    const justFlaggedNA = isFieldNA && !wasFieldNA.current;
    wasFieldNA.current = isFieldNA;

    if (!justFlaggedNA) return;

    // The server drops these values on the same tick, so drop them here too — otherwise stale
    // values stay on screen and their controls (multi-select chips) remain interactive.
    for (const { key, clearedValue } of collectClearableFieldKeys(field)) {
      if (!shouldClearFieldValue(clearedValue, valuesRef.current[key])) continue;

      setFieldValue(
        key,
        clearedValue,
        false, // the item is no longer validated, so skip revalidating it
      );
    }
  }, [isFieldNA, field, setFieldValue]);
};
